import { CalendarOffIcon } from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useAppointmentLabels } from '../hooks/useAppointmentLabels';

export interface CancelAppointmentDialogProps {
  visible: boolean;
  onNo: () => void;
  onYes: () => void;
}

export function CancelAppointmentDialog({
  visible,
  onNo,
  onYes,
}: Readonly<CancelAppointmentDialogProps>) {
  const colors = useUIColors();
  const labels = useAppointmentLabels();

  const handleYes = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.CANCEL_DIALOG_CONFIRM,
      eventType: 'action',
    });
    onYes();
  };

  return (
    <ContentDialog
      visible={visible}
      onClose={onNo}
      contentAlign="center"
      maxWidth={473}
      cardStyle={styles.card}
      headerStyle={styles.header}
      actionsStyle={styles.actions}
      subtitleStyle={styles.subtitle}
      icon={
        <View style={styles.iconWrap}>
          <CalendarOffIcon size={80} color={colors.fontPrimary} />
        </View>
      }
      title={labels.CANCEL_TITLE}
      titleVariant="title2"
      titleColor="primary"
      subtitle={labels.CANCEL_MESSAGE}
      subtitleVariant="body"
      subtitleColor="secondary"
      actions={[
        {
          label: labels.CANCEL_NO,
          variant: 'outline',
          flex: true,
          style: styles.noButton,
          onPress: () => {
            trackCatalogEvent({
              screen: ALMETLAA_SCREEN.APPOINTMENT,
              actionName: ALMETLAA_ACTION.APPOINTMENT.CANCEL_DIALOG_NO,
              eventType: 'action',
            });
            onNo();
          },
        },
        {
          label: labels.CANCEL_YES,
          variant: 'primary',
          flex: true,
          style: styles.yesButton,
          onPress: handleYes,
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: 473,
    minHeight: 312,
    gap: 32,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  header: {
    gap: 8,
    alignItems: 'center',
    width: '100%',
  },
  iconWrap: {
    marginBottom: 16,
  },
  subtitle: {
    textAlign: 'center',
    alignSelf: 'center',
    maxWidth: 360,
    width: '100%',
  },
  actions: {
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
  },
  noButton: {
    maxWidth: 204.5,
    width: '100%',
    height: 48,
    gap: 4,
    borderRadius: 4,
    borderWidth: 2,
    padding: 12,
  },
  yesButton: {
    maxWidth: 204.5,
    width: '100%',
    height: 48,
    gap: 4,
    borderRadius: 4,
    padding: 12,
  },
});

export default CancelAppointmentDialog;
